/**
 * Command Center Store
 *
 * Polls the unified /api/command-center/snapshot endpoint at a fixed cadence and
 * exposes the latest 6-quadrant snapshot. Poll-first (per the design gate): simpler
 * than SSE and equivalent for a single operator at 1-2s. All API calls go through
 * the centralized api.ts client — no direct fetch() here.
 */

import { create } from 'zustand';
import { commandCenterApi, ApiError } from '../lib/api';
import type { CommandCenterSnapshot } from '../lib/api';

const DEFAULT_INTERVAL_MS = 1500; // 1.5s — within the 1-2s target

interface CommandCenterState {
  snapshot: CommandCenterSnapshot | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
  _timer: ReturnType<typeof setInterval> | null;
  _inFlight: boolean;

  fetchOnce: () => Promise<void>;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

export const useCommandCenterStore = create<CommandCenterState>((set, get) => ({
  snapshot: null,
  loading: false,
  error: null,
  lastUpdated: null,
  _timer: null,
  _inFlight: false,

  fetchOnce: async () => {
    // Skip if a request is already in flight — never stack polls.
    if (get()._inFlight) return;
    set({ _inFlight: true, loading: get().snapshot === null });
    try {
      const snap = await commandCenterApi.snapshot();
      set({ snapshot: snap, error: null, lastUpdated: Date.now(), loading: false });
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      // Keep the last good snapshot on screen; just surface the error banner.
      set({ error: msg, loading: false });
    } finally {
      set({ _inFlight: false });
    }
  },

  startPolling: (intervalMs = DEFAULT_INTERVAL_MS) => {
    get().stopPolling();
    void get().fetchOnce();
    const timer = setInterval(() => { void get().fetchOnce(); }, intervalMs);
    set({ _timer: timer });
  },

  stopPolling: () => {
    const t = get()._timer;
    if (t) clearInterval(t);
    set({ _timer: null });
  },
}));
